import moment from 'moment';
import z from 'zod';

import { client } from '~shared/api';

import Status from '../interfaces/status';
import { WorkTimeTypes } from '../interfaces/work-time-types';
import { DeviationDto } from '../model/deviation.dto';
import { TabelRowChangeStatusDto } from '../model/tabel-row-change-status.dto';
import { TabelRowManualCreateDto } from '../model/tabel-row-manual-create.dto';
import { TabelRowDto, TabelRowDtoScheme } from '../model/tabel-row.dto';

export class TableFilter {
	employees_name?: string;
	section_id?: number;
	startDate?: Date;
	endDate?: Date;
	work_time_type?: WorkTimeTypes;
	schedule_name?: string;
	position_id?: string;
	withoutActualStay?: boolean;
	withoutCompletedRows?: boolean;
	constructor() {
		this.withoutCompletedRows = true;
		this.startDate = moment().subtract(1, 'day').startOf('day').toDate();
		this.endDate = moment().startOf('day').toDate();
	}
}

export async function loadTabelRows(
	filter: TableFilter,
	page?: number,
	limit?: number,
): Promise<{ data: TabelRowDto[]; total: number }> {
	const params = new URLSearchParams();

	if (page) params.append('page', page.toString());
	if (limit) params.append('limit', limit.toString());

	if (filter.employees_name) {
		params.append('userName', filter.employees_name);
	}

	if (filter.schedule_name) {
		params.append('scheduleName', filter.schedule_name);
	}

	if (filter.position_id) {
		params.append('positionId', filter.position_id);
	}

	if (filter.startDate) {
		params.append('startDate', moment(filter.startDate).format('YYYY-MM-DD'));
	}

	if (filter.endDate) {
		params.append('endDate', moment(filter.endDate).format('YYYY-MM-DD'));
	}

	if (filter.work_time_type) {
		params.append('workTimeType', filter.work_time_type.toString());
	}
	if (filter.section_id) {
		params.append('sectionId', filter.section_id.toString());
	}

	if (filter.withoutActualStay) {
		params.append('withoutActualStay', filter.withoutActualStay.toString());
	}

	if (filter.withoutCompletedRows) {
		params.append('withoutCompletedRows', filter.withoutCompletedRows.toString());
	}

	const url = `/tabel?${params.toString()}`;
	const response = await client.get(url);
	const { data, total } = response as { data: unknown[]; total: number };
	return { data: z.array(TabelRowDtoScheme).parse(data), total };
}

export async function changeStatusForTabelRows(dtos: TabelRowChangeStatusDto[], status: Status) {
	const params = new URLSearchParams({
		status: status,
	});

	return client.put(`/tabel/status?${params.toString()}`, dtos);
}

export async function createManualTabelRows(dto: TabelRowManualCreateDto[]) {
	return client.post('/tabel', dto);
}

export async function deleteManualTabelRows(ids: number[]) {
	const params = new URLSearchParams();

	ids.forEach(id => {
		params.append('ids', id.toString());
	});

	return client.del(`/tabel?${params.toString()}`);
}

export async function acceptChangesTabelRows(dto: TabelRowDto[]) {
	await client.put('/tabel', dto);
}

export async function getDeviation(id: number): Promise<DeviationDto | null> {
	return client.get(`/tabel/deviation/${id}`);
}

export async function regenerateTabelByUser(userId: number, startDate: Date, endDate: Date) {
	const params = new URLSearchParams({
		userId: userId.toString(),
		startDate: moment(startDate).format('YYYY-MM-DD'),
		endDate: moment(endDate).format('YYYY-MM-DD'),
	});
	return client.post(`/tabel/regenerate-by-user?${params.toString()}`);
}
